// Input rules for the dose calculator. Client mirror of
// doseInputVerdict / parseDoseInput in server/dose-calc.js.
//
// The server is the authority and refuses independently. This exists so
// the UI never posts a request it knows is invalid, and so the live
// preview cannot flash a dose figure built on a half-typed number.

// A typed measurement, or null when the field carries no measurement.
//
// An explicit 0 is data. An EMPTY field is not. Number('') is 0 and
// passes Number.isFinite, which is how a blank box became a measured
// zero and produced a dose plan for a never-tested parameter.
//
// '-' and '.' are rejected too: the preview fires on every keystroke, so
// those transient states would otherwise post as 0 while the keeper is
// still typing.
export function parseMeasurement(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Whether the preview request should be sent at all. `hasReading` is
// whether the parameter has ever been tested on this tank; without one
// there is no baseline to scale a dose off.
export function doseFormValid({ current, target, hasReading }) {
  if (!hasReading) return false;
  return parseMeasurement(current) !== null && parseMeasurement(target) !== null;
}

// Which inputs a preview was computed for. The old figure stays on screen
// while the next preview is on its way, and its "I dosed N mL" used to
// stay live: a tap mid-edit logged the old mL against the new numbers
// (nightly sweep, 2026-10-11). Confirm only when this matches the screen.
export function doseInputsKey({ current, target, product }) {
  return `${parseMeasurement(current)}|${parseMeasurement(target)}|${product || ''}`;
}

// What is left in a bottle the keeper starts tracking. An emptied "Left
// now" box means "I did not say", so the bottle counts as full; Number('')
// read it as 0 and saved a full bottle as empty, which then warned it was
// running out. An explicit 0 stays 0.
export function bottleRemaining(size, left) {
  const s = parseMeasurement(size);
  if (s === null || s <= 0) return null;
  const l = parseMeasurement(left);
  return l !== null && l >= 0 && l <= s ? l : s;
}

// The value the "current" field starts with. Empty when no reading
// exists: showing the target in a field labelled current puts a number
// the keeper never measured in front of them as if they had.
export function initialCurrentField(latestValue) {
  return latestValue === null || latestValue === undefined ? '' : String(latestValue);
}

// Warning copy for a baseline the server does not fully trust. Names
// the source and the age, because "your reading may be stale" without
// either is unactionable: the keeper cannot tell whether to re-test or
// to check the probe.
export function sourceDoubtWarning(doubt, paramLabel) {
  if (!doubt) return null;
  if (doubt.reason === 'unchanged_automated') {
    const hrs = Number(doubt.hours_since_change);
    const age = Number.isFinite(hrs) ? `${hrs} hours` : 'a long time';
    const src = doubt.method === 'hydros' ? 'HYDROS' : 'Apex';
    return `This ${paramLabel} value came from your ${src} and has not changed in ${age}. That can mean a very stable tank, or a tester that has stopped testing. Confirm it is current before dosing against it.`;
  }
  if (doubt.reason === 'test_time_unknown') {
    return `This ${paramLabel} value came from your Trident, and TankSensei cannot tell when it was tested: a Trident that has stopped testing keeps handing on its last number. Doses since the test may not show in it either. Test by hand, or confirm the number is current, before dosing against it.`;
  }
  if (doubt.reason === 'trident_disagrees') {
    const u = doubt.unit ? ` ${doubt.unit}` : '';
    return `Your own ${paramLabel} test read ${doubt.own_value}${u}, but your Trident reads ${doubt.trident_value}${u}. One of them is off, and dosing on a misread test pushes the tank the wrong way. Test again before dosing, or confirm your test is right.`;
  }
  if (doubt.reason === 'unconfirmed_outlier' && doubt.typed) {
    return `The ${paramLabel} number you entered is far from your recent tests. A misread test or a slip is more likely than a sudden change, so check it before dosing.`;
  }
  if (doubt.reason === 'unconfirmed_outlier') {
    return `Your last ${paramLabel} reading is far from your recent ones. A misread test is more likely than a sudden change, so test again before dosing, or confirm the number is right.`;
  }
  if (doubt.reason === 'old_reading') {
    const hrs = Number(doubt.hours_since_reading);
    const age = Number.isFinite(hrs) ? `${Math.round(hrs / 24)} days` : 'a while';
    return `Your last ${paramLabel} test was ${age} ago. The tank has moved since then — retest before dosing, or confirm you want to dose against the old number.`;
  }
  return null;
}

const DOSE_ERRORS = {
  no_reading_logged: 'Log a test for this parameter first.',
  reading_implausible:
    'Your last reading for this looks like a salinity number (specific gravity), not a real test of it. Test again and log the new result before dosing.',
  current_implausible:
    'That looks like a salinity reading (specific gravity), not a test of this. Check the number and enter it again; log a salinity reading on Home.',
  stale_source_unconfirmed: 'Confirm the reading is current before logging this dose.',
  current_required: 'Enter your current measured value.',
  target_required: 'Enter a target value.',
  no_working_volume: 'Set your tank water volume first.',
  unsupported_param: 'This parameter cannot be dosed.',
  no_product_for_param: 'Pick a product for this parameter.',
  custom_product_changed:
    'Your own product was changed since this dose was worked out. Check the new number before you dose.',
  custom_product_invalid:
    'Your own product needs checking before it can be dosed. Edit it, or use one of ours.',
  not_a_reef_tank:
    'This tank does not use reef dosing. Log anything you add from Log, Dose instead.',
  dose_unit_mismatch:
    'This card is not in the unit the dose calculator works in (dKH for alkalinity, ppm for calcium and magnesium), so it cannot work out a dose from it. Send us feedback (the speech bubble at the top) and we will move the card over.',
  client_ref_mismatch:
    'A different dose was already logged from this screen. Check Recent doses, then log this one again if you gave it.',
};
const DOSE_UNIT_SHOWN = { alk: 'dKH', ca: 'ppm', mg: 'ppm' };

// A typed Current the server refused as no kit's number: an S.G.-shaped
// one gets the salinity hint, anything else the server's own words for it
// ("2 is not a calcium reading"), never the salinity hint.
export function doseRefusalMessage(payload, fallback) {
  if (payload?.error === 'dose_unit_mismatch' && payload.unit && DOSE_UNIT_SHOWN[payload.param]) {
    const need = DOSE_UNIT_SHOWN[payload.param];
    return `This card is in ${payload.unit}, and the dose calculator works in ${need}, so it cannot work out a dose from it. Send us feedback (the speech bubble at the top) and we will move the card to ${need}.`;
  }
  if (
    payload?.error === 'current_implausible' &&
    payload.reason !== 'looks_like_salinity' &&
    typeof payload.message === 'string' &&
    payload.message
  ) {
    return payload.message;
  }
  return doseErrorMessage(payload?.error, fallback);
}

// Words for a server code. `fallback` is for a caller (logging a dose) whose
// unknown errors are not a calculation problem; a server sentence passes
// through, a bare code never does.
export function doseErrorMessage(code, fallback) {
  if (DOSE_ERRORS[code]) return DOSE_ERRORS[code];
  if (fallback) return code && /\s/.test(String(code)) ? String(code) : fallback;
  return 'Could not calculate a dose. Check your entries.';
}

// What "I dosed it" says once the server has it. A repeat of a save that
// already went through says when it was logged, so "Logged" never reads as
// a second dose in the log (the review of nightly sweep 2, 2026-10-24).
export function confirmedDoseMessage({ event, amountMl, label, retestTask, now = new Date() }) {
  const retest = retestTask ? ' Retest scheduled.' : '';
  const when = event?.duplicate && event.ts ? loggedAtWords(event.ts, now) : null;
  if (when) {
    return `Already logged at ${when}: ${amountMl} mL of ${label}. It is in your log once.${retest}`;
  }
  return `Logged ${amountMl} mL of ${label}.${retest}`;
}

// When a dose already in the log was logged, in the phone's own clock: the
// time alone today, with the day otherwise.
export function loggedAtWords(ts, now = new Date()) {
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return null;
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return d.toDateString() === now.toDateString()
    ? time
    : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
}
